import react, { useState, useEffect, useRef } from "react";
import moment from "moment-timezone";
import { useDispatch, useSelector } from "react-redux";
import MessageViewBox from "../layouts/sdk/MessageViewBox";
import ChatInputBox from "../layouts/sdk/ChatInputBox";
import { useSearchParams } from "react-router-dom";
import { getChatHistory, getTenantWaba, tenantContactUpsert } from "../store/actions/tenantAction";
import SelectCountry from "../components/Dropdowns/SelectCountry";
import MobileInput from "../components/MobileInput";
import { useTranslation } from "react-i18next";
import { generateToken } from "../store/actions/loginAction";
import { LOGIN_INFO, WABA } from "../utils/types";
import { getCountry, saveSelectedWaba } from "../store/actions/globalAction";
import socket from "../utils/socket";
import LiveChatPreview from "../layouts/tenant/LiveChatPreview";
import { decryptData } from "../utils/helper";

function LiveChatBot() {
    let dispatch = useDispatch();
    const { t } = useTranslation();
    const lastItemRef = useRef(null);
    const [inboxSettings, setInboxSettings] = useState({});
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const [messageList, setMessageList] = useState([]);
    const [searchParams] = useSearchParams();
    const [canSendMsg, setCanSendMsg] = useState(true);
    const globalReducer = useSelector((state) => state.globalReducer);
    const [savedContact, setSavedContact] = useState({ phoneCode: 968 });
    const savedContactRef = useRef();

    useEffect(() => {
        getToken();
        const conContact = JSON.parse(localStorage.getItem("web-live-chat-contact") ?? "{}");
        setSavedContact({ ...conContact, phoneCode: conContact?.phoneCode ?? 968 });
        savedContactRef.current = conContact;
    }, []);

    const getToken = () => {
        const tokenParams = {
            "x-tenant-id": searchParams.get("tenantId"),
            "x-api-key": decryptData(searchParams.get("apiKey"))
        };
        dispatch(generateToken({}, { client_id: "iomnihub-tenant" }, (d) => {
            localStorage.setItem("sessionToken", d?.access_token);
            localStorage.setItem("refreshToken", d?.refresh_token);
            const [header, payload, signature] = d?.access_token?.split(".");
            const decodedPayload = JSON.parse(atob(payload));
            const InfoParse = JSON.parse(atob(decodedPayload?.info));
            dispatch({ type: LOGIN_INFO, payload: InfoParse });
            dispatch(getTenantWaba(
                {
                    tenantId: searchParams.get("tenantId"),
                    criteria: { active: true, id: Number(searchParams.get("tenantWabaId")) },
                },
                (d) => {
                    dispatch({ type: WABA, payload: d?.data[0] });
                    dispatch(saveSelectedWaba(d?.data[0]));
                }
            ));
        }, tokenParams));
    }

    useEffect(() => {
        if (globalReducer?.loginInfo?.tenantId && searchParams.get("tenantWabaId")) {
            if (socket?.connected) {
                socket.emit("messageInitiate", {
                    tenantId: globalReducer.loginInfo?.tenantId,
                    wabaId: searchParams.get("tenantWabaId"),
                    userUid: globalReducer.loginInfo?.uid
                });
            } else {
                socket.on("connect", () => {
                    socket.emit("messageInitiate", {
                        tenantId: globalReducer.loginInfo?.tenantId,
                        wabaId: searchParams.get("tenantWabaId"),
                        userUid: globalReducer.loginInfo?.uid
                    });
                });
            }
            socket.on("message", (msg) => {
                const msgObj = JSON.parse(msg);
                if (msgObj && msgObj.messageText && msgObj?.contactId === savedContactRef.current?.id) {
                    msgObj.messageText = JSON.parse(msgObj.messageText);
                    setMessageList((prevMessages) => [...prevMessages, msgObj]);
                }
            });

            getChatingHistory();
            if (!globalReducer.country || globalReducer.country?.length <= 0) {
                dispatch(getCountry({}, (c) => { }));
            }
        }
        return () => {
            socket.off("customerEvent");
        };
    }, [globalReducer.loginInfo]);

    const getChatingHistory = () => {
        if (!savedContact?.id) return;
        dispatch(getChatHistory({
            filter: [{ property: "messageStatus", operator: "EQ", not: true, value: "blocked" }],
            criteria: {
                contactId: savedContact?.id
            }, orderBy: [{ property: "id", direction: "DESC" }],
            size: 100, offset: 0,
        }, searchParams.get("tenantWabaId"), (h) => {
            if (Array.isArray(h)) {
                setMessageList(h.map(m => {
                    m.messageText = JSON.parse(m?.messageText);
                    return m;
                })?.sort((a, b) => moment(a?.createdAt) - moment(b?.createdAt)));
            }
        }));
    };

    useEffect(() => {
        setTimeout(() => focusLastItem(), 50);
    }, [messageList?.length]);

    const saveContact = (e) => {
        e.preventDefault();
        savedContact.tenantId = globalReducer?.loginInfo?.tenantId;
        savedContact.uid = globalReducer?.loginInfo?.uid;
        savedContact.createdForm = "WEBLIVECHAT";
        savedContact.whatsappNumber = (savedContact?.phoneCode ?? 968) + savedContact?.phone + "-web";
        if (!savedContact?.category || savedContact?.category?.length < 1) {
            savedContact.category = ["GENERIC_INTERNAL"];
        }
        dispatch(
            tenantContactUpsert(savedContact, (a) => {
                localStorage.setItem("web-live-chat-contact", JSON.stringify(a));
                setSavedContact(a);
                savedContactRef.current = a;
                setTimeout(() => getChatingHistory(), 100);
            })
        );
    };

    const groupedData = messageList?.reduce((acc, item) => {
        const date = moment(item.createdAt).format("YYYY-MM-DD");
        if (!acc[date]) {
            acc[date] = [];
        }
        acc[date].push(item);
        return acc;
    }, {});

    const formatDate = (date) => {
        const givenDate = moment.utc(date).tz(timezone).startOf("day");
        const today = moment().utc().tz(timezone).startOf("day");
        const tomorrow = moment()
            .utc()
            .tz(timezone)
            .add(-1, "day")
            .startOf("day");

        if (givenDate.isSame(today, "day")) {
            return "Today";
        } else if (givenDate.isSame(tomorrow, "day")) {
            return "Yesterday";
        } else {
            return givenDate.format("dddd, MMMM D, YYYY");
        }
    };
    const focusLastItem = () => {
        lastItemRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
        });
    };

    return (
        <div className={`flex flex-col justify-between hidescroller w-full overflow-y-auto h-[calc(100%-90px)]`}>
            <div className="services h-full">
                <div className="flex flex-col w-full h-full mx-auto shadow-lg">
                    <div className={`flex justify-start items-center text-white`} style={{ backgroundColor: searchParams.get("primary") }}>
                        <div className="text-white px-4 py-3 w-full font-semibold">
                            {savedContact?.whatsappNumber ? savedContact?.name + " ( " + savedContact?.whatsappNumber + " )" : searchParams.get("title") || "iOmniHub Support"}
                        </div>
                        <div className="flex items-center gap-2 pr-3">
                            {/* <button onClick={() => console.log("Minimize clicked")} className="p-1 rounded hover:bg-white/20 transition">
                                <i className="fa-solid fa-window-minimize"></i>
                            </button> */}
                            <button onClick={() => {
                                try {
                                    window.parent.postMessage("CLOSE_CHAT", "*");
                                } catch { }
                            }} className="p-1 rounded hover:bg-white/20 transition">
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>
                    </div>
                    {savedContact?.whatsappNumber ? <div className="fixed bottom-0 left-0 w-full">
                        <div className="h-[496px] overflow-y-auto">
                            <div className="flex-1 py-2 px-1 space-y-2">
                                <LiveChatPreview isOpenWebLiveChat={true} inboxSettings={inboxSettings} messageList={messageList} contact={{ contact: savedContact }} setCanSendMsg={setCanSendMsg} />
                            </div>
                        </div>
                        <ChatInputBox searchParams={searchParams} selectedContact={savedContact} />
                    </div> : <div className="flex justify-center items-center h-full w-full px-4 py-4">
                        <form onSubmit={saveContact}>
                            <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700">
                                Enter your name and contact number to start chat
                            </label>
                            <div className="mt-2">
                                <input type="text" required className="border border-gray-300 rounded-md py-2 text-[14px] w-[100%]"
                                    placeholder="Enter your name here"
                                    value={savedContact?.name || ""}
                                    onChange={(e) => setSavedContact({ ...savedContact, name: e.target.value })}
                                />
                            </div>
                            <div className="mt-2">
                                <MobileInput className="py-2"
                                    required={true}
                                    countryList={globalReducer.country}
                                    phoneCode={savedContact?.phoneCode}
                                    phone={savedContact?.phone}
                                    setPhoneCode={(phoneCode) => setSavedContact({ ...savedContact, phoneCode })}
                                    setPhone={(phone) => setSavedContact({ ...savedContact, phone })}
                                    placeholder={t("Enter Phone Number")}
                                />
                            </div>
                            <div className="mt-4 flex justify-center">
                                <button type="submit"
                                    className="justify-center items-center flex px-2 h-10 py-1 font-semibold text-[14px] text-white rounded cursor-pointer" style={{ backgroundColor: searchParams.get("primary") }}>
                                    Start Chat
                                </button>
                            </div>
                        </form>
                    </div>}
                </div>
            </div>
        </div>
    )
}

export default LiveChatBot
